import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Self-hosted fonts via @fontsource — local npm-managed copies that
// don't hang on fonts.googleapis.com when that host is slow or
// blocked. Switched 2026-05-23: Geist (sans) + Geist Mono (data
// columns) replace Inter + JetBrains Mono as part of the landing
// brand sync. Geist's slashed-zero (via the ``zero`` OpenType
// feature, see index.css body) keeps numeric cells unambiguous in
// the tight monospace rows. (2026-06 sweep: all hard-coded
// JetBrains Mono references replaced with Geist Mono; the
// Inter / JetBrains @fontsource deps remain installed, now unused.)
import "@fontsource/geist-sans/400.css";
import "@fontsource/geist-sans/500.css";
import "@fontsource/geist-sans/600.css";
import "@fontsource/geist-sans/700.css";
import "@fontsource/geist-mono/400.css";
import "@fontsource/geist-mono/500.css";
import "@fontsource/geist-mono/600.css";
import App from "./App";
import "./index.css";
import { useAuth } from "./stores/auth";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { installChunkReloadGuard } from "./lib/chunkReload";

// Stale-chunk auto-recovery: after a dashboard deploy the content-hashed
// chunk filenames change, so an already-open tab running the previous
// bundle 404s on the next lazy-route navigation and (pre-2026-05-30)
// went black until a manual reload. Install the window-level guard at
// boot; the ErrorBoundary below is the render-time backstop.
installChunkReloadGuard();

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 2000,
      retry: 1,
    },
  },
});

// Kick off the /api/auth/me hydration once at boot. Route guards
// render a "Loading…" splash until ``status`` flips to "ready",
// avoiding redirect-flicker on deep links for authed users.
useAuth.getState().hydrate();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={qc}>
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
